import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Zap, Lock, PhoneCall, FileText, Wallet, TrendingUp, CheckCircle2, Star, Sparkles, Building2, User2, Users, ChevronRight, Play, Download } from "lucide-react";
import MarketingLayout from "@/components/layout/MarketingLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } } };

const features = [
  { icon: Zap, title: "Salary-linked disbursals", body: "Products designed around your monthly salary cycle for effortless repayment." },
  { icon: Lock, title: "Bank-grade security", body: "AES-256 encryption at rest, TLS in transit, and role-based access with audit trails." },
  { icon: FileText, title: "Fully paperless KYC", body: "Aadhaar, PAN and selfie — verified digitally. No branch visits." },
  { icon: TrendingUp, title: "Transparent pricing", body: "See interest, fees and total cost before you sign. No hidden charges." },
  { icon: ShieldCheck, title: "Configurable underwriting", body: "Every application is reviewed by our credit engine and human reviewers." },
  { icon: PhoneCall, title: "Human-first support", body: "Chat with a real person via WhatsApp, live chat or phone — 7 days a week." },
];

const steps = [
  { n: "01", title: "Create account", body: "Register in seconds with your email and mobile number." },
  { n: "02", title: "Verify KYC", body: "Upload Aadhaar, PAN and a selfie — digitally verified." },
  { n: "03", title: "Choose product", body: "Salary Advance or Personal Loan — pick tenure and amount." },
  { n: "04", title: "Get a decision", body: "Track your application in real time; disbursal follows approval." },
];

const testimonials = [
  { name: "Aarti V.", role: "Product Manager, Bengaluru", body: "Refreshingly transparent. I could see exactly what I would pay before signing.", stars: 5 },
  { name: "Rohan M.", role: "Software Engineer, Pune", body: "The salary advance saved me during a family emergency. Application was smooth and fast.", stars: 5 },
  { name: "Sneha K.", role: "Consultant, Mumbai", body: "Beautiful app, clear repayment schedule, and lovely support. Highly recommend.", stars: 5 },
];

export default function Home() {
  const [email, setEmail] = useState("");
  const [subLoading, setSubLoading] = useState(false);

  const subscribe = async () => {
    if (!email) return;
    setSubLoading(true);
    try {
      await api.post("/subscribe", { email });
      toast.success("You're on the list!");
      setEmail("");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not subscribe"); }
    finally { setSubLoading(false); }
  };

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="hero-orb h-96 w-96 bg-indigo-500/30" style={{ top: "-6rem", left: "-6rem" }} />
        <div className="hero-orb h-[28rem] w-[28rem] bg-violet-500/30" style={{ top: "2rem", right: "-6rem" }} />
        <div className="hero-orb h-72 w-72 bg-sky-400/20" style={{ bottom: "-6rem", left: "20%" }} />

        <div className="page-shell px-6 pt-16 pb-24 relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div initial="hidden" animate="show" variants={stagger} className="lg:col-span-7 space-y-8">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 rounded-full glass px-3 py-1.5 text-xs">
              <Sparkles className="h-3.5 w-3.5 text-primary" /> New — Zero-fee salary advance for the first month
            </motion.div>
            <motion.h1 variants={fadeUp} className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Lending, <span className="brand-gradient-text">reimagined</span> for the salaried professional.
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-muted-foreground max-w-xl">
              Finzpe helps you access funds when you need them — with transparent pricing, thoughtful underwriting, and a beautifully simple experience.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <Link to="/register"><Button size="lg" className="rounded-full brand-gradient text-white h-12 px-7" data-testid="hero-apply">Apply Now <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
              <Link to="/emi-calculator"><Button size="lg" variant="outline" className="rounded-full h-12 px-7" data-testid="hero-emi"><Play className="mr-2 h-4 w-4" /> Try EMI Calculator</Button></Link>
            </motion.div>
            <motion.div variants={fadeUp} className="grid grid-cols-3 gap-4 pt-4 max-w-lg">
              {[{k: "200K+", v: "Customers"}, {k: "₹850Cr", v: "Disbursed*"}, {k: "4.8★", v: "User rating"}].map((s) => (
                <div key={s.k} className="rounded-xl glass p-4">
                  <div className="font-display text-2xl">{s.k}</div>
                  <div className="text-xs text-muted-foreground">{s.v}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute inset-0 rounded-3xl brand-gradient blur-3xl opacity-30" />
              <Card className="relative rounded-3xl glass p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Live Preview</div>
                    <div className="font-display text-lg">Your indicative offer</div>
                  </div>
                  <div className="h-10 w-10 rounded-xl brand-gradient flex items-center justify-center text-white"><Wallet className="h-5 w-5" /></div>
                </div>
                <div className="rounded-2xl bg-muted p-5">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">You may be eligible for</div>
                  <div className="font-display text-3xl mt-1">₹1,50,000</div>
                  <div className="text-xs text-muted-foreground mt-1">at 14.5% p.a. · 12 months · illustrative</div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="rounded-xl bg-background border border-border p-3"><div className="text-muted-foreground">EMI</div><div className="font-medium">₹13,510</div></div>
                  <div className="rounded-xl bg-background border border-border p-3"><div className="text-muted-foreground">Interest</div><div className="font-medium">₹12,120</div></div>
                  <div className="rounded-xl bg-background border border-border p-3"><div className="text-muted-foreground">Total</div><div className="font-medium">₹1.62L</div></div>
                </div>
                <Link to="/register"><Button className="w-full rounded-full brand-gradient text-white">Get my real offer</Button></Link>
                <div className="text-[10px] text-muted-foreground text-center">Offers are illustrative and subject to underwriting.</div>
              </Card>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Finzpe */}
      <section className="page-shell px-6 py-24" id="why">
        <div className="max-w-2xl mb-14">
          <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-3">Why Finzpe</div>
          <h2 className="font-display text-4xl lg:text-5xl leading-tight">Six reasons professionals choose us over legacy lenders.</h2>
        </div>
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f) => (
            <motion.div key={f.title} variants={fadeUp}>
              <Card className="p-6 card-hover h-full">
                <div className="h-11 w-11 rounded-xl brand-gradient text-white flex items-center justify-center mb-4"><f.icon className="h-5 w-5" /></div>
                <div className="font-display text-lg mb-1">{f.title}</div>
                <div className="text-sm text-muted-foreground">{f.body}</div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Loan features / products */}
      <section className="page-shell px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Card className="lg:col-span-7 relative overflow-hidden rounded-3xl p-8 lg:p-12 brand-gradient text-white">
            <div className="hero-orb h-64 w-64 bg-white/30" style={{ top: "-3rem", right: "-3rem" }} />
            <div className="relative z-10 max-w-md space-y-5">
              <div className="text-xs uppercase tracking-widest opacity-90">Featured product</div>
              <h3 className="font-display text-3xl">Salary Advance Loan</h3>
              <p className="text-white/90">Bridge the gap till your next paycheck. Amounts up to ₹1L, tenure aligned to your salary cycle, no lengthy paperwork.</p>
              <div className="flex gap-3 flex-wrap">
                <div className="rounded-full bg-white/15 border border-white/25 px-3 py-1 text-xs">Up to ₹1,00,000</div>
                <div className="rounded-full bg-white/15 border border-white/25 px-3 py-1 text-xs">1–3 months</div>
                <div className="rounded-full bg-white/15 border border-white/25 px-3 py-1 text-xs">18% p.a. onwards</div>
              </div>
              <Link to="/salary-advance"><Button variant="secondary" className="rounded-full text-primary" data-testid="home-explore-salary">Explore product <ChevronRight className="ml-1 h-4 w-4" /></Button></Link>
            </div>
          </Card>
          <Card className="lg:col-span-5 relative overflow-hidden rounded-3xl p-8 lg:p-12 border border-primary/20">
            <div className="space-y-5">
              <div className="text-xs uppercase tracking-widest text-primary font-semibold">Personal Loan</div>
              <h3 className="font-display text-3xl">Bigger goals. Longer tenure.</h3>
              <p className="text-muted-foreground">Fund a wedding, a home upgrade, or a well-earned trip. Up to ₹5L at rates from 14% p.a., over 6–36 months.</p>
              <ul className="text-sm space-y-2">
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Flexible tenures</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Prepayment allowed</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Fixed EMI</li>
              </ul>
              <Link to="/personal-loan"><Button className="rounded-full brand-gradient text-white" data-testid="home-explore-personal">Explore product <ChevronRight className="ml-1 h-4 w-4" /></Button></Link>
            </div>
          </Card>
        </div>
      </section>

      {/* How it works */}
      <section className="page-shell px-6 py-24">
        <div className="max-w-2xl mb-14">
          <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-3">How it works</div>
          <h2 className="font-display text-4xl lg:text-5xl leading-tight">A calm, four-step experience.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {steps.map((s, i) => (
            <div key={s.n} className="relative">
              <div className="rounded-2xl border border-border p-6 bg-card/60 h-full card-hover">
                <div className="text-xs uppercase tracking-widest text-primary font-semibold">Step {s.n}</div>
                <div className="font-display text-xl mt-2">{s.title}</div>
                <div className="text-sm text-muted-foreground mt-2">{s.body}</div>
              </div>
              {i < steps.length - 1 && <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-[2px] step-line" />}
            </div>
          ))}
        </div>
      </section>

      {/* Eligibility + Docs */}
      <section className="page-shell px-6 py-24 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <Card className="p-8 rounded-3xl">
          <div className="text-xs uppercase tracking-widest text-primary font-semibold">Eligibility</div>
          <h3 className="font-display text-3xl mt-3">Who can apply?</h3>
          <ul className="space-y-3 mt-6 text-sm">
            {["Salaried professional aged 21–60", "Minimum monthly income ₹20,000", "Valid Aadhaar + PAN", "Active salary bank account", "Indian citizen with permanent address"].map((t) => (
              <li key={t} className="flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-primary mt-0.5" /><span>{t}</span></li>
            ))}
          </ul>
          <div className="mt-8 flex gap-3">
            <Link to="/eligibility"><Button className="rounded-full brand-gradient text-white">Check eligibility</Button></Link>
            <Link to="/loan-calculator"><Button variant="outline" className="rounded-full">Loan Calculator</Button></Link>
          </div>
        </Card>
        <Card className="p-8 rounded-3xl">
          <div className="text-xs uppercase tracking-widest text-primary font-semibold">Required Documents</div>
          <h3 className="font-display text-3xl mt-3">What you'll need</h3>
          <div className="grid grid-cols-2 gap-3 mt-6">
            {[
              { icon: User2, label: "Aadhaar" },
              { icon: FileText, label: "PAN" },
              { icon: User2, label: "Selfie" },
              { icon: FileText, label: "Salary Slip" },
              { icon: Building2, label: "Bank Statement" },
              { icon: FileText, label: "Address Proof" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="rounded-xl border border-border p-4 flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Icon className="h-4 w-4" /></div>
                <div className="text-sm font-medium">{label}</div>
              </div>
            ))}
          </div>
        </Card>
      </section>

      {/* Benefits */}
      <section className="page-shell px-6 py-16">
        <Card className="rounded-3xl p-8 lg:p-14 relative overflow-hidden">
          <div className="hero-orb h-72 w-72 bg-primary/20" style={{ top: "-3rem", left: "10%" }} />
          <div className="hero-orb h-64 w-64 bg-secondary/20" style={{ bottom: "-3rem", right: "5%" }} />
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
            <div>
              <div className="text-xs uppercase tracking-widest text-primary font-semibold">Member benefits</div>
              <h3 className="font-display text-3xl mt-3">More than just a loan.</h3>
            </div>
            <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {["Free credit health report", "Prepayment without penalty*", "Loyalty rate reductions", "Priority customer support"].map((b) => (
                <div key={b} className="flex items-start gap-3"><Star className="h-5 w-5 text-primary mt-0.5" /><span className="text-sm">{b}</span></div>
              ))}
            </div>
          </div>
        </Card>
      </section>

      {/* Testimonials */}
      <section className="page-shell px-6 py-24">
        <div className="max-w-2xl mb-10">
          <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-3">Customer stories</div>
          <h2 className="font-display text-4xl lg:text-5xl">Loved by discerning professionals.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {testimonials.map((t) => (
            <Card key={t.name} className="p-6 card-hover h-full">
              <div className="flex gap-1 mb-3">{Array.from({ length: t.stars }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current text-amber-500" />)}</div>
              <p className="text-sm">{t.body}</p>
              <div className="mt-6 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full brand-gradient text-white font-display flex items-center justify-center">{t.name[0]}</div>
                <div>
                  <div className="text-sm font-medium">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.role}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* FAQs */}
      <section className="page-shell px-6 py-24 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-1">
          <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-3">FAQs</div>
          <h2 className="font-display text-4xl leading-tight">Answers to common questions.</h2>
          <Link to="/faqs" className="inline-flex items-center gap-2 text-primary text-sm mt-6" data-testid="home-faq-all">See all FAQs <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="lg:col-span-2">
          <Accordion type="single" collapsible className="w-full" data-testid="home-faqs-accordion">
            {[
              { q: "How does Finzpe decide loan amounts?", a: "Our credit engine uses income, employment stability, credit history, and internal risk models. A human reviewer confirms edge cases." },
              { q: "Can I prepay my loan?", a: "Yes. Personal loans allow prepayment; charges (if any) are shared upfront on your sanction letter." },
              { q: "Is my data safe?", a: "Yes. Data is encrypted end-to-end, access is role-based, and every action is audit-logged." },
              { q: "What is a salary advance?", a: "A short-term loan (1–3 months) linked to your monthly salary, ideal for bridging temporary gaps." },
              { q: "How long does disbursal take?", a: "Approved loans are processed as configurable business workflows — timelines depend on verification." },
            ].map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left" data-testid={`home-faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Download App */}
      <section className="page-shell px-6 py-16">
        <Card className="rounded-3xl brand-gradient text-white p-8 lg:p-14 relative overflow-hidden">
          <div className="hero-orb h-72 w-72 bg-white/30" style={{ top: "-4rem", right: "-2rem" }} />
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div className="space-y-4">
              <div className="text-xs uppercase tracking-widest opacity-80">Coming soon</div>
              <h3 className="font-display text-4xl">Take Finzpe wherever you go.</h3>
              <p className="text-white/90 max-w-md">Apply, track, and repay from your phone. Push notifications on approvals and EMI dues.</p>
              <div className="flex gap-3 flex-wrap">
                <Button className="rounded-full bg-black text-white hover:bg-black/90"><Download className="h-4 w-4 mr-2" /> App Store</Button>
                <Button className="rounded-full bg-white text-primary hover:bg-white/90"><Download className="h-4 w-4 mr-2" /> Google Play</Button>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="w-56 h-96 rounded-[36px] bg-black/30 border border-white/20 p-3 backdrop-blur-xl">
                <div className="w-full h-full rounded-[28px] bg-gradient-to-br from-white/10 to-white/5 p-5 flex flex-col justify-between">
                  <div className="text-xs uppercase tracking-widest opacity-80">Finzpe</div>
                  <div>
                    <div className="text-xs opacity-70">Active loan</div>
                    <div className="font-display text-3xl">₹85,000</div>
                    <div className="text-xs opacity-70 mt-1">Next EMI · 5th Mar</div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-[10px] opacity-90">
                    <div className="rounded-lg bg-white/10 p-2">Apply</div>
                    <div className="rounded-lg bg-white/10 p-2">Pay</div>
                    <div className="rounded-lg bg-white/10 p-2">Track</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </section>

      {/* Contact / Newsletter */}
      <section className="page-shell px-6 py-24 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <Card className="rounded-3xl p-8">
          <div className="text-xs uppercase tracking-widest text-primary font-semibold">Contact</div>
          <h3 className="font-display text-3xl mt-3">Speak to a human.</h3>
          <p className="text-sm text-muted-foreground mt-2">Our team is available Mon–Sat, 9am–7pm IST.</p>
          <div className="mt-6 grid gap-3 text-sm">
            <div className="flex items-center gap-3"><Users className="h-4 w-4 text-primary" /> hello@finzpe.com</div>
            <div className="flex items-center gap-3"><PhoneCall className="h-4 w-4 text-primary" /> +91 87000 59442</div>
          </div>
          <Link to="/contact" className="inline-flex items-center gap-2 text-primary text-sm mt-6" data-testid="home-contact">Open contact form <ArrowRight className="h-4 w-4" /></Link>
        </Card>
        <Card className="rounded-3xl p-8">
          <div className="text-xs uppercase tracking-widest text-primary font-semibold">Newsletter</div>
          <h3 className="font-display text-3xl mt-3">Money habits, monthly.</h3>
          <p className="text-sm text-muted-foreground mt-2">Get curated finance ideas in your inbox. Unsubscribe any time.</p>
          <div className="mt-6 flex gap-2">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@work.com" data-testid="home-newsletter-email" />
            <Button onClick={subscribe} disabled={subLoading} className="brand-gradient text-white rounded-full" data-testid="home-newsletter-submit">Subscribe</Button>
          </div>
        </Card>
      </section>
    </MarketingLayout>
  );
}
